import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { Button } from '@/components/ui/button'

export function CreatorSettings() {
  const { user } = useAuth()
  const [displayName, setDisplayName] = useState(user?.displayName || user?.name || '')
  const [bio, setBio] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setTimeout(() => {
      setSaving(false)
      setMessage('Settings saved locally. Connect this screen to the backend to persist changes.')
      setTimeout(() => setMessage(null), 4000)
    }, 800)
  }

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Creator settings</h1>
        <p className="mt-1 text-sm text-gray-400">
          Configure how your creator profile appears across Binyuma. This screen currently updates
          values only in your browser.
        </p>
      </div>

      {message && (
        <div className="rounded-lg border border-emerald-700 bg-emerald-900/30 px-3 py-2 text-xs text-emerald-200">
          {message}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4 rounded-xl border border-gray-800 bg-black/40 p-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Creator name
          </label>
          <input
            type="text"
            className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How your name appears to viewers"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Contact email
          </label>
          <input
            type="email"
            className="w-full rounded-lg border border-gray-800 bg-gray-900 px-3 py-2 text-sm text-gray-300"
            value={user?.email || ''}
            disabled
          />
          <p className="mt-1 text-[11px] text-gray-500">
            Contact email comes from your main Binyuma account. Updating this will be supported later.
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Bio
          </label>
          <textarea
            rows={4}
            className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell viewers a bit about you and your stories."
          />
        </div>
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </form>

      <div className="rounded-xl border border-gray-800 bg-black/40 p-4 space-y-2 text-xs text-gray-400">
        <p className="font-medium text-gray-200">Creator Agreement</p>
        {user?.hasAcceptedCreatorAgreement && user?.creatorAgreementAcceptedAt ? (
          <p>
            You have accepted the Creator Agreement on{' '}
            <span className="text-gray-300">
              {new Date(user.creatorAgreementAcceptedAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
            .{' '}
            <Link to="/creator/agreement" className="text-[#D4AF37] hover:underline">
              View agreement
            </Link>
          </p>
        ) : (
          <p>
            You have not yet signed the Creator Agreement.{' '}
            <Link to="/creator/agreement" className="text-[#D4AF37] hover:underline">
              Sign the Creator Agreement
            </Link>
          </p>
        )}
      </div>

      <div className="rounded-xl border border-gray-800 bg-black/40 p-4 space-y-2 text-xs text-gray-400">
        <p className="font-medium text-gray-200">Monetisation & payouts</p>
        <p>
          Creator monetisation details and payout configuration will be added here once the business
          model is finalised. For now, reach out to the Binyuma team if you have questions about
          revenue sharing.
        </p>
      </div>
    </div>
  )
}

